// BUILD-046 section D (Owner direction of 2026-10-21): where presets are kept.
//
// The app has no server-side user-preference store for My Work (the sort, the density and Show requester are all kept in the browser, and the launcher says its pinned apps will move to a
// UserPreferences table later). Presets follow the same established place: THIS BROWSER (localStorage), one record per signed-in person AND per tab:
//     mywork-presets:v1:<employee id>:<tab>
// So they survive a reload, one person never sees or changes another person's presets, and a tab never sees another tab's. The limitation is real and is stated to the person: presets
// are not synced to another browser or device and are lost if the browser data is cleared. Nothing here is a permission: a stored preset is read through parseBook, so a tampered record can
// only ever name a filter or a sort, never data. With no signed-in person there is nothing to key by, so nothing is read or written.
//
// Pure module over an injected storage. Erasable TypeScript only.
import { EMPTY_BOOK, parseBook } from "./presetsModel";
import type { PresetBook, PresetTab } from "./presetsModel";

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const PRESETS_KEY_PREFIX = "mywork-presets:v1:";

/** The key of one person on one tab; null when there is no person. */
export function presetsKey(userId: string | null | undefined, tab: PresetTab): string | null {
  return typeof userId === "string" && userId.trim() !== "" ? PRESETS_KEY_PREFIX + userId.trim() + ":" + tab : null;
}

/** The stored book of a person on a tab; an empty book when none, or when the record cannot be read. Never throws. */
export function loadBook(storage: KeyValueStorage | null, userId: string | null | undefined, tab: PresetTab): PresetBook {
  const key = presetsKey(userId, tab);
  if (storage === null || key === null) return EMPTY_BOOK;
  try {
    const raw = storage.getItem(key);
    return raw === null ? EMPTY_BOOK : parseBook(tab, JSON.parse(raw));
  } catch {
    return EMPTY_BOOK;
  }
}

/** Stores the book; false when it could not be kept (no person, no storage, a full or blocked store) - the caller says so, nothing is pretended. */
export function saveBook(storage: KeyValueStorage | null, userId: string | null | undefined, tab: PresetTab, book: PresetBook): boolean {
  const key = presetsKey(userId, tab);
  if (storage === null || key === null) return false;
  try {
    storage.setItem(key, JSON.stringify(book));
    return true;
  } catch {
    return false;
  }
}

/** The browser storage, or null where it is not available. */
export function browserStorage(): KeyValueStorage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Said to the person once, where presets are kept. */
export const PRESETS_STORAGE_NOTE = "Presets are saved in this browser for you. They are not shared with other people, and they do not follow you to another browser or device.";
